import { TeamRole } from '@showbie-socrative/socrative-utils';

import { useTeams } from '~teacher/hook/team/useTeams';
import { useMultipleTeamMembers } from '~teacher/hook/team/useTeamsMembers';

/**
 * Hook that checks if the current user owns any teams with members.
 * Used to prevent account deletion when a user owns teams with active members.
 * Returns query objects for loading/error states and a boolean indicating if deletion should be blocked.
 */
export function useIsOwnerOfTeamWithMembers() {
  // Query to get all teams where the current user is an owner
  // Returns an array of team IDs where user has owner role
  const teamsQuery = useTeams({
    select: (teams) => {
      return teams
        .filter((team) => team.role === TeamRole.Owner)
        .map((team) => team.id);
    },
  });

  // For each team where user is owner, query the number of team members
  // Only runs when teamsQuery has data
  // Returns array of queries, each containing member count for a team
  const teamMemberQueries = useMultipleTeamMembers({
    teamIds: teamsQuery.data,
    enabled: !!teamsQuery.data,
    select: (members) => members.length,
  });

  // Determines if account deletion should be blocked
  // Returns true if ANY team owned by user has more than 1 member
  // This prevents owners from deleting their account while still having active team members
  const cannotDelete = (teamMemberQueries ?? []).some(
    (teamMemberQuery) => teamMemberQuery?.data > 1
  );

  return {
    // Return all queries for loading/error states
    queries: [teamsQuery, ...(teamMemberQueries ?? [])],
    // Return whether deletion should be blocked
    cannotDelete,
  };
}
